import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import { Icon } from '#app/components/ui/icon'
import {
	NavigationMenu,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	navigationMenuTriggerStyle,
} from '#app/components/ui/navigation-menu'
import { buildCategoryTree } from '#app/utils/pz/navigation/categoryTree'
import { type NavigationItem } from '#app/utils/pz/navigation/resolveNavigation'
import { publicRoutes } from '#app/utils/pz/routes'

export type MarketingNavCategory = {
	id: string
	name: string
	slug: string
	description?: string | null
	parentId?: string | null
	position?: number
}

type NavMenuProps = React.ComponentProps<typeof NavigationMenu> & {
	categories?: MarketingNavCategory[]
	marketingLinks?: NavigationItem[]
}

export const NavMenu = ({
	categories,
	marketingLinks,
	...props
}: NavMenuProps) => {
	const { t } = useTranslation()
	const items = Array.isArray(marketingLinks) ? marketingLinks : []
	const learnLabel = t('marketing.nav.learn')
	const additionalLinks = items.filter(
		(item) =>
			item.href !== publicRoutes.courses.list() &&
			item.name.trim().toLowerCase() !== learnLabel.trim().toLowerCase(),
	)

	const { rootCategories, childCategoriesByParentId } = useMemo(() => {
		return buildCategoryTree(categories)
	}, [categories])

	return (
		<NavigationMenu {...props}>
			<NavigationMenuList className="gap-6 space-x-0 data-[orientation=vertical]:flex-col data-[orientation=vertical]:items-start">
				<NavigationMenuItem>
					<NavigationMenuLink asChild>
						<Link
							to={publicRoutes.catalog.index()}
							className={navigationMenuTriggerStyle()}
							reloadDocument
						>
							{t('marketing.nav.catalog')}
						</Link>
					</NavigationMenuLink>
				</NavigationMenuItem>

				<NavigationMenuItem>
					{rootCategories.length > 0 ? (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<button
									type="button"
									className={`${navigationMenuTriggerStyle()} inline-flex items-center gap-2`}
								>
									{t('marketing.nav.learn')}
									<Icon name="chevron-down" />
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="start" className="w-[320px]">
								{rootCategories.map((cat) => {
									const children = childCategoriesByParentId.get(cat.id) ?? []
									const catHref = `${publicRoutes.courses.list()}?category=${cat.slug}`

									if (children.length === 0) {
										return (
											<DropdownMenuItem key={cat.id} asChild>
												<Link to={catHref} reloadDocument>
													<div className="flex flex-col">
														<span className="text-sm font-medium">
															{cat.name}
														</span>
														{cat.description ? (
															<span className="text-muted-foreground line-clamp-2 text-xs">
																{cat.description}
															</span>
														) : null}
													</div>
												</Link>
											</DropdownMenuItem>
										)
									}

									return (
										<DropdownMenuSub key={cat.id}>
											<DropdownMenuSubTrigger>
												<div className="flex flex-col">
													<span className="text-sm font-medium">
														{cat.name}
													</span>
													{cat.description ? (
														<span className="text-muted-foreground line-clamp-2 text-xs">
															{cat.description}
														</span>
													) : null}
												</div>
											</DropdownMenuSubTrigger>
											<DropdownMenuSubContent className="w-[320px]">
												<DropdownMenuItem asChild>
													<Link to={catHref} reloadDocument>
														{cat.name}
													</Link>
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												{children.map((child) => {
													const childHref = `${publicRoutes.courses.list()}?subcategory=${child.slug}`
													return (
														<DropdownMenuItem key={child.id} asChild>
															<Link to={childHref} reloadDocument>
																<div className="flex flex-col">
																	<span className="text-sm font-medium">
																		{child.name}
																	</span>
																	{child.description ? (
																		<span className="text-muted-foreground line-clamp-2 text-xs">
																			{child.description}
																		</span>
																	) : null}
																</div>
															</Link>
														</DropdownMenuItem>
													)
												})}
											</DropdownMenuSubContent>
										</DropdownMenuSub>
									)
								})}

								<DropdownMenuSeparator />
								<DropdownMenuItem asChild>
									<Link to={publicRoutes.catalog.index()} reloadDocument>
										{t('marketing.nav.viewFullCatalog')}
									</Link>
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					) : (
						<NavigationMenuLink asChild>
							<Link
								to={publicRoutes.catalog.index()}
								className={navigationMenuTriggerStyle()}
								reloadDocument
							>
								{t('marketing.nav.learn')}
							</Link>
						</NavigationMenuLink>
					)}
				</NavigationMenuItem>

				{additionalLinks.map((item) => (
					<NavigationMenuItem key={item.id}>
						<NavigationMenuLink asChild>
							{item.target || item.href.startsWith('http') ? (
								<a
									href={item.href}
									target={item.target}
									rel={item.target === '_blank' ? 'noreferrer' : undefined}
									className={navigationMenuTriggerStyle()}
								>
									{item.name}
								</a>
							) : (
								<Link to={item.href} className={navigationMenuTriggerStyle()}>
									{item.name}
								</Link>
							)}
						</NavigationMenuLink>
					</NavigationMenuItem>
				))}
			</NavigationMenuList>
		</NavigationMenu>
	)
}
